"use client";

import { Suspense, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import axios from "axios";
import CategoryFilterChips from "@/components/ui/CategoryFilterChips";

interface RecipeListItem {
  id: number;
  keyword_id: number;
  keyword: string;
  title: string;
  youtube_url: string;
  channel_name: string | null;
  view_count: number | null;
  difficulty: string | null;
  lifecycle_stage: string | null;
  categories: string[];
  has_recipe_data?: boolean;   // #10 — 재료/조리 정제 완료 여부
}

interface SuggestResponse {
  query: string;
  suggestions: { keyword: string; recipe_count: number }[];
}

type SortKey = "recommended" | "view" | "recent";
type StageKey = "" | "emerging" | "growing" | "mature" | "declining";

// #3 default = recommended (라이프사이클 stage round-robin) — 매번 같은 영상 노출 해소
const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "recommended", label: "🎯 추천" },
  { key: "view", label: "조회수 ↑" },
  { key: "recent", label: "최근 등록" },
];

const STAGE_OPTIONS: { key: StageKey; label: string }[] = [
  { key: "", label: "전체" },
  { key: "emerging", label: "🌱 태동" },
  { key: "growing", label: "🔥 성장" },
  { key: "mature", label: "📊 성숙" },
  { key: "declining", label: "📉 하락" },
];

const STAGE_BADGE: Record<string, { label: string; cls: string }> = {
  emerging: { label: "🌱 태동", cls: "bg-green-500/10 text-green-400" },
  growing: { label: "🔥 성장", cls: "bg-orange-500/10 text-orange-400" },
  mature: { label: "📊 성숙", cls: "bg-blue-500/10 text-blue-400" },
  declining: { label: "📉 하락", cls: "bg-gray-500/10 text-gray-400" },
};

const PAGE_SIZE = 20;

function youtubeThumb(url: string): string | null {
  const m = url.match(/(?:v=|youtu\.be\/|embed\/)([^&?/]+)/);
  return m ? `https://i.ytimg.com/vi/${m[1]}/mqdefault.jpg` : null;
}

export default function RecipesListPage() {
  return (
    <Suspense fallback={<p className="text-text-sub text-center py-8">불러오는 중...</p>}>
      <RecipesListInner />
    </Suspense>
  );
}

function RecipesListInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const activeCategory = searchParams.get("category") ?? "";
  const activeQ = searchParams.get("q") ?? "";
  const sortParam = (searchParams.get("sort") as SortKey | null) ?? "recommended";
  const activeSort: SortKey =
    SORT_OPTIONS.some((o) => o.key === sortParam) ? sortParam : "recommended";
  const stageParam = (searchParams.get("stage") as StageKey | null) ?? "";
  const activeStage: StageKey =
    STAGE_OPTIONS.some((o) => o.key === stageParam) ? stageParam : "";

  const [items, setItems] = useState<RecipeListItem[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchInput, setSearchInput] = useState(activeQ);
  const [suggestions, setSuggestions] = useState<SuggestResponse["suggestions"]>([]);

  // URL ?q= 가 외부에서 바뀌면 입력값도 동기화
  useEffect(() => {
    setSearchInput(activeQ);
  }, [activeQ]);

  const setSort = (s: SortKey) => {
    const next = new URLSearchParams(searchParams.toString());
    if (s === "recommended") next.delete("sort");   // default → URL 깔끔
    else next.set("sort", s);
    const qs = next.toString();
    router.replace(qs ? `/recipes?${qs}` : "/recipes");
  };

  const setStage = (s: StageKey) => {
    const next = new URLSearchParams(searchParams.toString());
    if (!s) next.delete("stage");
    else next.set("stage", s);
    const qs = next.toString();
    router.replace(qs ? `/recipes?${qs}` : "/recipes");
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams.toString());
    const trimmed = searchInput.trim();
    if (trimmed) next.set("q", trimmed);
    else next.delete("q");
    const qs = next.toString();
    router.replace(qs ? `/recipes?${qs}` : "/recipes");
  };

  useEffect(() => {
    setLoading(true);
    setOffset(0);
    setSuggestions([]);
    const params: Record<string, string | number> = { sort: activeSort, limit: PAGE_SIZE, offset: 0 };
    if (activeCategory) params.category = activeCategory;
    if (activeQ) params.q = activeQ;
    if (activeStage) params.stage = activeStage;
    axios
      .get<RecipeListItem[]>("/api/recipes/list", { params })
      .then((r) => {
        setItems(r.data);
        setHasMore(r.data.length === PAGE_SIZE);
        // 검색어 있고 결과 0건이면 제안 로직 트리거
        if (activeQ && r.data.length === 0) {
          axios
            .get<SuggestResponse>("/api/recipes/suggest", { params: { q: activeQ } })
            .then((rs) => setSuggestions(rs.data.suggestions))
            .catch(() => setSuggestions([]));
        }
      })
      .catch(() => {
        setItems([]);
        setHasMore(false);
      })
      .finally(() => setLoading(false));
  }, [activeCategory, activeSort, activeQ, activeStage]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextOffset = offset + PAGE_SIZE;
      const params: Record<string, string | number> = {
        sort: activeSort,
        limit: PAGE_SIZE,
        offset: nextOffset,
      };
      if (activeCategory) params.category = activeCategory;
      if (activeQ) params.q = activeQ;
      if (activeStage) params.stage = activeStage;
      const r = await axios.get<RecipeListItem[]>("/api/recipes/list", { params });
      setItems([...items, ...r.data]);
      setOffset(nextOffset);
      setHasMore(r.data.length === PAGE_SIZE);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="pb-24">
      {/* 헤더 */}
      <div className="sticky top-0 z-40 bg-bg/90 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => router.back()} className="text-text-sub text-xl" aria-label="뒤로">
            ←
          </button>
          <h1 className="text-sm font-bold text-text flex-1">🍳 레시피</h1>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* 검색 */}
        <form onSubmit={submitSearch}>
          <div className="relative">
            <input
              value={searchInput}
              onChange={(e) => {
                const v = e.target.value;
                setSearchInput(v);
                // 비우면 즉시 복귀 (manufacturers·community 패턴)
                if (v === "" && activeQ !== "") {
                  const next = new URLSearchParams(searchParams.toString());
                  next.delete("q");
                  const qs = next.toString();
                  router.replace(qs ? `/recipes?${qs}` : "/recipes");
                }
              }}
              placeholder="제목·채널·키워드 검색 (예: 프렌치토스트, 백종원)"
              className="w-full bg-bg-secondary text-text text-sm pl-10 pr-9 py-2.5 rounded-full border border-border outline-none focus:border-primary/50"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-sub text-sm">🔍</span>
            {searchInput && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  if (activeQ) {
                    const next = new URLSearchParams(searchParams.toString());
                    next.delete("q");
                    const qs = next.toString();
                    router.replace(qs ? `/recipes?${qs}` : "/recipes");
                  }
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-sub text-sm hover:text-text"
                aria-label="검색 지우기"
              >
                ×
              </button>
            )}
          </div>
        </form>

        {/* 라이프사이클 단계 필터 — 단계 선택 시 키워드 트렌드 점수 desc 자동 정렬 */}
        <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar -mx-4 px-4 pb-1">
          <span className="text-2xs text-text-sub shrink-0">단계</span>
          {STAGE_OPTIONS.map((o) => {
            const active = o.key === activeStage;
            return (
              <button
                key={o.key || "all"}
                onClick={() => setStage(o.key)}
                className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap shrink-0 transition-colors ${
                  active
                    ? "bg-primary text-bg font-semibold"
                    : "text-text-sub hover:text-text border border-border"
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>

        {/* 카테고리 필터 */}
        <CategoryFilterChips />

        {/* 정렬 — 단계 필터 적용 시 트렌드 점수 자동 정렬이라 비활성 안내 */}
        {activeStage ? (
          <p className="text-2xs text-text-sub">
            ✨ 단계 필터 적용 중 — 키워드 트렌드 점수 순서로 자동 정렬 (키워드당 5개씩)
          </p>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-2xs text-text-sub">정렬</span>
            {SORT_OPTIONS.map((o) => {
              const active = o.key === activeSort;
              return (
                <button
                  key={o.key}
                  onClick={() => setSort(o.key)}
                  className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                    active
                      ? "bg-primary text-bg font-semibold"
                      : "text-text-sub hover:text-text"
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        )}

        {/* 리스트 */}
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="animate-pulse card flex gap-3">
                <div className="w-28 h-20 rounded-lg bg-bg-elevated shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-bg-elevated rounded w-3/4" />
                  <div className="h-3 bg-bg-elevated rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <>
            <div className="card text-center py-10">
              <p className="text-sm text-text-sub">
                {activeQ
                  ? `"${activeQ}" 검색 결과가 없어요.`
                  : activeCategory
                  ? `"${activeCategory}" 카테고리에 등록된 레시피가 아직 없어요.`
                  : "아직 등록된 레시피가 없어요."}
              </p>
              <p className="text-xs text-text-sub mt-1.5">
                {activeQ
                  ? suggestions.length > 0
                    ? "이런 키워드는 어때요?"
                    : "다른 키워드로 검색해 보세요."
                  : "유튜브 레시피 수집기가 돌면 순차로 채워져요."}
              </p>
            </div>

            {/* 제안 칩 (R1-5 후속 — 0건 시 비슷한 키워드 추천) */}
            {activeQ && suggestions.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {suggestions.map((s) => (
                  <button
                    key={s.keyword}
                    onClick={() => {
                      const next = new URLSearchParams(searchParams.toString());
                      next.set("q", s.keyword);
                      const qs = next.toString();
                      router.replace(qs ? `/recipes?${qs}` : "/recipes");
                    }}
                    className="chip border border-primary/30 text-primary hover:bg-primary/5 transition-colors"
                  >
                    #{s.keyword}
                    <span className="text-2xs text-text-sub ml-1">{s.recipe_count}건</span>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="space-y-2">
            {items.map((r) => {
              const thumb = youtubeThumb(r.youtube_url);
              const stage = r.lifecycle_stage ? STAGE_BADGE[r.lifecycle_stage] : null;
              // 현재 URL(카테고리·정렬 포함) 을 from 으로 보존 → 상세에서 목록 복귀 시 상태 유지 (R1-6)
              const qs = searchParams.toString();
              const fromHref = qs ? `/recipes?${qs}` : "/recipes";
              return (
                <Link
                  key={r.id}
                  href={`/recipe/${r.id}?from=${encodeURIComponent(fromHref)}`}
                  className="card flex gap-3 hover:border-primary/30 transition-colors"
                >
                  {thumb ? (
                    <img
                      src={thumb}
                      alt=""
                      className="w-28 h-20 rounded-lg object-cover bg-bg-secondary shrink-0"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-28 h-20 rounded-lg bg-bg-secondary shrink-0 flex items-center justify-center text-2xl">
                      🎬
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-text line-clamp-2 leading-snug">
                      {r.title}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <span className="text-2xs font-bold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
                        #{r.keyword}
                      </span>
                      {stage && (
                        <span className={`text-2xs px-1.5 py-0.5 rounded-full ${stage.cls}`}>
                          {stage.label}
                        </span>
                      )}
                      {r.has_recipe_data && (
                        <span className="text-2xs px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
                          재료·조리법
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 text-2xs text-text-sub">
                      {r.channel_name && <span>📺 {r.channel_name}</span>}
                      {r.view_count !== null && r.view_count > 0 && (
                        <span>· {(r.view_count / 10000).toFixed(1)}만 회</span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}

            {hasMore && (
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="w-full py-3 text-sm text-text-sub hover:text-text border border-border rounded-xl disabled:opacity-50"
              >
                {loadingMore ? "불러오는 중..." : "더 보기"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
